import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './styles/globals.css';
import Logo from './components/Logo';
import ScrollToTop from './components/ScrollToTop';
import AppLoader from './components/AppLoader';
import AuthGuard from './components/AuthGuard';
import { useAppLoader } from './hooks/useAppLoader';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import components (we'll create these)
import Home from './pages/Home';
import About from './pages/About';
import Programs from './pages/Programs';
import Jobs from './pages/Jobs';
import Donations from './pages/Donations';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import JoinMission from './pages/JoinMission';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminContacts from './pages/admin/Contacts';
import AdminVolunteers from './pages/admin/Volunteers';
import AdminUsers from './pages/admin/Users';

// Protected route component
import ProtectedAdminRoute from './components/ProtectedAdminRoute';

// Navigation component with auth integration
const Navigation: React.FC<{ isMenuOpen: boolean; setIsMenuOpen: (open: boolean) => void }> = ({ isMenuOpen, setIsMenuOpen }) => {
  const { isAuthenticated, user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-gradient-to-r from-green-600 to-green-800 text-white sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <Logo size="md" showText={true} />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-1 xl:space-x-6">
            <Link to="/" className="px-3 py-2 rounded-md text-sm font-medium hover:text-green-200 hover:bg-green-700 transition-all">Home</Link>
            <Link to="/about" className="px-3 py-2 rounded-md text-sm font-medium hover:text-green-200 hover:bg-green-700 transition-all">About</Link>
            <Link to="/programs" className="px-3 py-2 rounded-md text-sm font-medium hover:text-green-200 hover:bg-green-700 transition-all">Programs</Link>
            <Link to="/jobs" className="px-3 py-2 rounded-md text-sm font-medium hover:text-green-200 hover:bg-green-700 transition-all">Jobs</Link>
            <Link to="/donations" className="px-3 py-2 rounded-md text-sm font-medium hover:text-green-200 hover:bg-green-700 transition-all">Donate</Link>
            <Link to="/contact" className="px-3 py-2 rounded-md text-sm font-medium hover:text-green-200 hover:bg-green-700 transition-all">Contact</Link>
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-green-200">Welcome, {user?.name}</span>
                <button 
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-md text-sm font-medium bg-red-500 hover:bg-red-400 transition-all"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/login" className="px-3 py-2 rounded-md text-sm font-medium bg-green-500 hover:bg-green-400 transition-all">Login</Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {!isMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-green-700 rounded-b-lg">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium hover:text-green-200 hover:bg-green-600 transition-all">Home</Link>
              <Link to="/about" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium hover:text-green-200 hover:bg-green-600 transition-all">About</Link>
              <Link to="/programs" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium hover:text-green-200 hover:bg-green-600 transition-all">Programs</Link>
              <Link to="/jobs" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium hover:text-green-200 hover:bg-green-600 transition-all">Jobs</Link>
              <Link to="/donations" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium hover:text-green-200 hover:bg-green-600 transition-all">Donate</Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium hover:text-green-200 hover:bg-green-600 transition-all">Contact</Link>
              
              {isAuthenticated ? (
                <div className="px-3 py-2">
                  <p className="text-sm text-green-200 mb-2">Welcome, {user?.name}</p>
                  <button 
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium bg-red-500 hover:bg-red-400 transition-all"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Link to="/login" onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium bg-green-500 hover:bg-green-400 transition-all">Login</Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// App Routes Component with conditional navbar/footer
const AppRoutes: React.FC<{ isMenuOpen: boolean; setIsMenuOpen: (open: boolean) => void }> = ({ isMenuOpen, setIsMenuOpen }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Check if current route is auth pages (register/login)
  const isAuthPage = ['/login', '/register'].includes(location.pathname);
  
  // Show navbar and footer for authenticated users on non-auth pages OR for all users on home page
  const showNavAndFooter = (isAuthenticated && !isAuthPage) || (!isAuthPage && location.pathname === '/');

  if (isAuthPage) {
    // For auth pages (register/login), show only the page content without navbar/footer
    return (
      <div className="min-h-screen">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Navigation - only show for authenticated users */}
      {showNavAndFooter && (
        <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      )}

      {/* Main content */}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/donations" element={<Donations />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/join-mission" element={<JoinMission />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          {/* Admin Routes - Protected */}
          <Route path="/admin/dashboard" element={
            <ProtectedAdminRoute>
              <AdminDashboard />
            </ProtectedAdminRoute>
          } />
          <Route path="/admin/contacts" element={
            <ProtectedAdminRoute>
              <AdminContacts />
            </ProtectedAdminRoute>
          } />
          <Route path="/admin/volunteers" element={
            <ProtectedAdminRoute>
              <AdminVolunteers />
            </ProtectedAdminRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedAdminRoute>
              <AdminUsers />
            </ProtectedAdminRoute>
          } />
        </Routes>
      </main>

      {/* Responsive Footer - only show for authenticated users */}
      {showNavAndFooter && (
        <footer className="bg-gray-800 text-white py-12 px-4 mt-12">
          <div className="container mx-auto">
            <div className="text-center">
              {/* Footer Logo */}
              <div className="flex justify-center mb-6">
                <Logo size="xl" className="border-gray-600" />
              </div>
              
              <h3 className="text-xl sm:text-2xl font-bold mb-4 text-green-400">Yakjehti Pakistan Welfare Society</h3>
              <p className="text-sm sm:text-base mb-6 max-w-2xl mx-auto text-gray-300 leading-relaxed">
                United for Pakistan's Progress – Creating Jobs, Building Futures through manufacturing, welfare, and community programs.
              </p>
              
              {/* Footer Links */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-6 text-sm">
                <Link to="/about" className="text-gray-400 hover:text-green-400 transition-colors">About Us</Link>
                <Link to="/programs" className="text-gray-400 hover:text-green-400 transition-colors">Programs</Link>
                <Link to="/jobs" className="text-gray-400 hover:text-green-400 transition-colors">Jobs</Link>
                <Link to="/donations" className="text-gray-400 hover:text-green-400 transition-colors">Donate</Link>
                <Link to="/contact" className="text-gray-400 hover:text-green-400 transition-colors">Contact</Link>
              </div>
              
              <div className="border-t border-gray-700 pt-6">
                <p className="text-xs sm:text-sm text-gray-400">Made with 💚 for Pakistan | © {new Date().getFullYear()} Yakjehti Pakistan Welfare Society</p>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

// Main App Content Component
const AppContent: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isLoading } = useAppLoader({
    minLoadingTime: 2500, // Show loader for at least 2.5 seconds
    checkAuthenticationState: true,
    skipLoaderOnSubsequentVisits: true // Only show on first visit or page refresh
  });

  const handleLoadingComplete = () => {
    // Additional logic can be added here when loading completes
    console.log('App loading completed');
  };

  return (
    <>
      {/* App Loader - Shows on first load or page refresh */}
      <AppLoader isVisible={isLoading} onLoadingComplete={handleLoadingComplete} />
      
      {/* Main App Content - Hidden while loading */}
      <div className={`transition-opacity duration-500 ${
        isLoading ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}>
        <Router>
          <ScrollToTop />
          <AuthGuard>
            <AppRoutes isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
          </AuthGuard>
          
        </Router>
      </div>
    </>
  );
};

// Main App wrapper with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
